import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  Lightbulb as HintIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getMockExamApiPrefix, getMockExamPortalHomePath } from '../../../api';
import QuestionPaperLayout from '../../QuestionBank/components/QuestionPaperLayout';
import HtmlWithLatex from '../../QuestionBank/components/HtmlWithLatex';
import FormatInstructions from '../../../components/FormatInstructions';
import VideoExplanationPlayer from '../../../components/VideoExplanationPlayer';
import MarksBreakdownTable from '../components/MarksBreakdownTable';
import InteractiveAnswerBlock from '../components/InteractiveAnswerBlock';
import ImageOverlayFillInput from '../components/ImageOverlayFillInput';
import {
  isFillQuestionType,
  coerceFillAnswersArray,
  isPerBlankFillDisplay,
} from '../../../utils/fillQuestionUtils';
import { fillBlanksAreInterchangeable, completeTruncatedMixedCorrectAnswer } from '../../../utils/answerMatchUtils';
import { blankIsCorrect } from '../utils/blankMatch';
import { resolveLeafVerdict } from '../utils/leafMarkVerdict';
import { reviewYourAnswerWasCorrectLabel } from '../utils/reviewEmbeddedCorrectAnswer';
import { toMockLeafRow } from '../utils/leafRow';
import { fmtMarks, formatAnswerLabel, plainLinesToHtml } from '../utils/breakdownFormat';
import { reconcileRowFiguresForDisplay } from '../../../utils/groupedQuestionFigures';

// Editing a question replaces its option rows, so an attempt taken beforehand holds option IDs
// that no longer exist. The server flags that rather than handing back the bare number, which the
// review page used to print as though the student had written "107859".
const ORPHANED_OPTION_NOTE =
  '<em>Not shown &mdash; this question&rsquo;s options were changed after the paper was taken.</em>';

// A choice question is answered by picking an option; drawing geometry left on it from earlier authoring
// must not draw a "Your shape / Correct shape" board under it (NLIGSC G1 2014 mock Q30a).
const isChoiceType = (type) => ['multiple-choice', 'multiple-select', 'true-false'].includes(String(type || '').toLowerCase());

const formatSubmittedWithSelectedIndex = (q, formatted) => {
  if (q?.submitted_option_orphaned) return ORPHANED_OPTION_NOTE;
  if (!Number.isInteger(q?.selected_index) || !Array.isArray(q?.options)) {
    return formatted;
  }
  const opt = q.options[q.selected_index];
  if (!opt) return formatted;
  const letter = ['A', 'B', 'C', 'D', 'E'][q.selected_index] ?? String(q.selected_index + 1);
  const text = opt.option_text ?? '';
  return text ? `${letter}: ${text}` : letter;
};

const ScanNoteAlert = ({ note }) => {
  if (!note?.message) return null;
  return (
    <Alert
      severity={note.kind === 'skipped' ? 'info' : 'warning'}
      sx={{ mb: 1.5, borderRadius: 2, py: 0.25 }}
    >
      <Typography variant="body2">{note.message}</Typography>
    </Alert>
  );
};

// Strip embedded sub-question labels (a) … b) …) from a grouped passage/stem.
// When the AI stores the full question in the parent, the passage should only show the context.
// Renders the student's plotted shape and the correct shape for an interactive drawing answer.
const cleanStemText = (text) => {
  if (!text) return text;
  const mA = text.match(/(?:\s|^)(a\s*[).]\s+\S)/i);
  const mB = text.match(/\s+b\s*[).]\s+\S/i);
  if (mA && mB && mA.index > 30) {
    return text.substring(0, mA.index).trimEnd();
  }
  return text;
};

// Extract the core question text from a sub-question that includes passage preamble.
// Finds the last occurrence of the sub-label (e.g. "a)" / "b)") at a line boundary
// and returns everything after it, discarding the repeated context.
const extractSubCore = (text, label) => {
  if (!text || !label) return text;
  const esc = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`(?:^|[\\n\\r])[ \\t]*${esc}[ \\t]*[).][ \\t]*`, 'gi');
  let lastMatch = null;
  let m;
  while ((m = pattern.exec(text)) !== null) {
    lastMatch = m;
  }
  if (lastMatch && lastMatch.index > 5) {
    return text.substring(lastMatch.index + lastMatch[0].length).trim();
  }
  return text;
};

const formatAnswer = (value) => {
  if (value == null || value === '') return '—';
  // A JSON coordinate array is a drawn shape, not a list of values — dumping the raw points into the
  // answer line is unreadable, and the shape itself is rendered by InteractiveAnswerBlock below.
  // Same collapse the Result and Report pages already do via breakdownFormat.
  if (typeof value === 'string') {
    const collapsed = formatAnswerLabel(value);
    if (collapsed === 'Shape drawn') return collapsed;
  }
  if (Array.isArray(value)) {
    // Nested arrays are drawing coordinates ([[x,y],…] / [[[x,y],[x,y]],…]) — shown by InteractiveAnswerBlock.
    if (value.some((v) => Array.isArray(v) || (v && typeof v === 'object'))) return 'Shape drawn';
    const joined = value.map((v) => String(v ?? '').trim()).filter(Boolean).join('\n\n');
    return joined || '—';
  }
  if (typeof value === 'object') {
    // Drawing payloads ({points, edges}, {cut, offsets}, {interactive}) — the shape renders below.
    if (!('text' in value) && ['points', 'edges', 'lines', 'cut', 'interactive'].some((k) => k in value)) {
      return 'Shape drawn';
    }
    // TypeDraw answer: { text, drawing } — show only the typed text
    if ('text' in value || 'drawing' in value) {
      const text = value.text;
      if (text) return String(text);
      if (value.drawing) return '[Drawing only]';
      return '—';
    }
    // A matching answer is a { leftItem: rightItem } map — read it back as the links the student drew,
    // in the same "a → b" shape the correct answer is shown in.
    const entries = Object.entries(value).filter(([, v]) => String(v ?? '').trim() !== '');
    if (entries.length > 0 && entries.every(([, v]) => typeof v === 'string')) {
      return entries.map(([k, v]) => `${k} → ${v}`).join('; ');
    }
    return entries
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join(' | ');
  }
  return String(value);
};

// One chip per leaf verdict, so the five states the resolver produces are all visible — the page
// used to colour the answer text only, which showed "written" and "unanswered" as the same grey.
const LeafMarks = ({ leaf }) => (
  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
    {fmtMarks(leaf.earned)} / {fmtMarks(leaf.max)}
  </Typography>
);

const VerdictChip = ({ leaf }) => {
  const { result, isEquiv, aiDecided } = leaf;
  if (result === 'unanswered') {
    return <Chip label="Not answered" size="small" variant="outlined" sx={{ fontWeight: 600, height: 22 }} />;
  }
  if (result === 'written') {
    return <Chip icon={<HintIcon sx={{ fontSize: 13 }} />} label="Written" size="small" sx={{ fontWeight: 600, height: 22 }} />;
  }
  if (result === 'correct') {
    return (
      <Chip
        icon={aiDecided ? <AiIcon sx={{ fontSize: 13 }} /> : <CheckIcon sx={{ fontSize: 13 }} />}
        label={aiDecided ? 'AI Correct' : isEquiv ? 'Equiv.' : 'Correct'}
        size="small"
        color="success"
        sx={{ fontWeight: 700, height: 22 }}
      />
    );
  }
  if (result === 'partial') {
    return (
      <Chip
        label="Partially correct"
        size="small"
        sx={{ fontWeight: 700, height: 22, bgcolor: '#fef3c7', color: '#92400e' }}
      />
    );
  }
  return (
    <Chip
      icon={aiDecided ? <AiIcon sx={{ fontSize: 13 }} /> : <WrongIcon sx={{ fontSize: 13 }} />}
      label={aiDecided ? 'AI Incorrect' : 'Incorrect'}
      size="small"
      color="error"
      sx={{ fontWeight: 700, height: 22 }}
    />
  );
};

// ---- AI Feedback Panel for a single question ----
// An image-fill answer is a set of boxes ON the picture. Listed as "value → value" rows the student
// could not tell which box each pair belonged to (HABS 2009 Q27d), so show the picture twice — their
// answers in the boxes, and the correct ones — the way the past-paper review does.
const cleanOverlayValue = (v) => {
  const raw = Array.isArray(v) ? (v[0] ?? '') : (v ?? '');
  return String(raw).replace(/&nbsp;/gi, ' ').replace(/<[^>]*>/g, '').trim();
};
const ImageFillReviewPair = ({ row, allCorrect = false, parent = null }) => {
  const overlay = row?.answer_data?.overlay_layout;
  const type = String(row?.type || row?.question_type || '').toLowerCase().replace(/_/g, '-');
  if (type !== 'image-fill' || overlay?.mode === 'inline' || !Array.isArray(overlay?.blanks) || overlay.blanks.length < 2) {
    return null;
  }
  const correct = overlay.blanks.map((_, i) => cleanOverlayValue((row.answer_data?.correct_blanks || [])[i]));
  const submitted = coerceFillAnswersArray(row.submitted_answer, correct).map((v) => cleanOverlayValue(v));
  // Per-box colours, judged like the "your answer → correct answer" rows below (the overlay paints
  // every read-only box green otherwise). The row's own Correct verdict wins, as it does there.
  const pooled = fillBlanksAreInterchangeable(row) || (parent ? fillBlanksAreInterchangeable(parent) : false);
  const matches = submitted.map((v, i) => (v === '' ? null
    : (allCorrect || blankIsCorrect(row, i, v, correct[i], { pooled, displayList: correct, submittedList: submitted }))));
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>Your answer</Typography>
        {submitted.some((v) => v !== '') ? (
          <ImageOverlayFillInput overlayLayout={overlay} values={submitted} revealValues={submitted} prefilled={row.answer_data?.blank_labels} blankMatches={matches} readOnly />
        ) : (
          <Typography variant="body1" color="text.disabled">—</Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>Correct answer</Typography>
        <ImageOverlayFillInput overlayLayout={overlay} values={correct} revealValues={correct} prefilled={row.answer_data?.blank_labels} readOnly />
      </Box>
    </Box>
  );
};

// A drawing row's stored answer is the drawing itself — shaded region ids, plotted points — which
// InteractiveAnswerBlock already draws. Printed as text it read as a column of stray numbers under
// "Your answer" (HABS 2009 Q25a). Only a typed part ({ interactive, text }) is text to show.
const typedPartOfAnswer = (row, formatted) => {
  const isInteractive = !!(row?.correct_interactive || row?.answer_data?.interactive || row?.your_answer_interactive);
  const raw = row?.submitted_answer;
  if (!isInteractive || raw == null || typeof raw === 'string') return formatted;
  if (!Array.isArray(raw) && typeof raw === 'object' && typeof raw.text === 'string') return raw.text;
  return '';
};

const AiFeedbackPanel = ({ questionId, aiFeedbackMap, aiFeedbackStatus, questionMarks, ruleIsCorrect, ruleIsPartial, forceIncorrect, unanswered }) => {
  const aiq = aiFeedbackMap?.[String(questionId)];

  if (aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') {
    return (
      <Box
        sx={{
          mt: 1.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: '#f3f0ff',
          border: '1px solid #e0d7ff',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={14} sx={{ color: '#7c3aed' }} />
        <Typography variant="caption" sx={{ color: '#7c3aed', fontStyle: 'italic' }}>
          AI feedback is being generated…
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === 'failed' || !aiq) {
    return null;
  }

  const aiIsCorrect = aiq.is_correct;
  const partialScore = aiq.partial_score;
  const isAiPartial = partialScore != null && partialScore > 0 && partialScore < 1;

  // Conflict: AI says incorrect/partial, but rule-based says fully correct.
  // `forceIncorrect` is the resolver's final word — a row it graded wrong (deterministically wrong
  // submission, explicit server Incorrect) must never be re-coloured green by a stale AI verdict.
  const aiConflictsWithRuleCorrect = !forceIncorrect && ruleIsCorrect === true && aiIsCorrect !== true;

  // Conflict: AI says incorrect, but rule-based says partially correct.
  // e.g. student typed "31", correct is "31, 62" — AI marks wrong, rule says partial.
  // The rule-based scorer has precise multi-value logic; trust it over AI here.
  // Either way round: a stored AI "Correct" over a row the marks scored partial printed a green
  // "AI FEEDBACK Correct" beside the partial mark.
  const aiConflictsWithRulePartial = !forceIncorrect && ruleIsPartial === true && ruleIsCorrect !== true
    && (aiIsCorrect === false || aiIsCorrect === true) && !isAiPartial;

  const anyConflict = aiConflictsWithRuleCorrect || aiConflictsWithRulePartial;

  // Effective verdict: when there's a conflict, trust the rule-based result
  let effectiveCorrect = forceIncorrect ? false : aiIsCorrect;
  let effectivePartial = forceIncorrect ? false : isAiPartial;

  if (aiConflictsWithRuleCorrect) {
    effectiveCorrect = true;
    effectivePartial = false;
  } else if (aiConflictsWithRulePartial) {
    effectiveCorrect = false;
    effectivePartial = true;
  }

  const earnedMarks = partialScore != null
    ? Math.round((partialScore * (questionMarks ?? 1)) * 100) / 100
    : null;

  let bgcolor = '#f8f9fa';
  let borderColor = '#e0e0e0';
  if (effectiveCorrect === true) { bgcolor = '#f0fdf4'; borderColor = '#bbf7d0'; }
  else if (effectivePartial) { bgcolor = '#fffbeb'; borderColor = '#fde68a'; }
  else if (effectiveCorrect === false) { bgcolor = '#fff5f5'; borderColor = '#fecaca'; }

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor,
        border: '1px solid',
        borderColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
        <AiIcon sx={{ fontSize: 15, color: '#7c3aed' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          AI Feedback
        </Typography>
        {effectiveCorrect === true && !effectivePartial && (
          <Chip
            icon={<CheckIcon sx={{ fontSize: 13 }} />}
            label="Correct"
            size="small"
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {effectivePartial && (
          <Chip
            label={
              aiConflictsWithRulePartial
                ? 'Partially correct'
                : `Partial — ${earnedMarks != null ? earnedMarks : Math.round(partialScore * 100) + '%'} marks`
            }
            size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {effectiveCorrect === false && !effectivePartial && (
          <Chip
            icon={<WrongIcon sx={{ fontSize: 13 }} />}
            label="Incorrect"
            size="small"
            sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {aiConflictsWithRuleCorrect && (
          <Chip
            label="Re-evaluated: Correct"
            size="small"
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {aiConflictsWithRulePartial && (
          <Chip
            label="Re-evaluated: Partially correct"
            size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>
      {unanswered ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          Not answered — the feedback below is about the question, not an answer you gave.
        </Typography>
      ) : aiConflictsWithRuleCorrect ? (
        <Typography variant="body2" sx={{ color: '#166534', lineHeight: 1.6 }}>
          Correct.
        </Typography>
      ) : aiConflictsWithRulePartial ? (
        <Typography variant="body2" sx={{ color: '#92400e', lineHeight: 1.6 }}>
          {aiIsCorrect === true
            ? 'Partially correct — part of the full answer is missing. Compare with the correct answer.'
            : 'Partially correct — your answer matches a required value.'}
        </Typography>
      ) : forceIncorrect && aiIsCorrect === true ? (
        // The stored sentence is the AI's "Correct." — printing it under an Incorrect chip contradicts
        // the mark (St Paul's Girls 2025 P1 Q22f: "Written 0/1" beside "AI FEEDBACK Correct").
        <Typography variant="body2" sx={{ color: '#991b1b', lineHeight: 1.6 }}>
          Incorrect — compare your answer with the correct answer.
        </Typography>
      ) : aiq.feedback ? (
        <Box sx={{ fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.6, mb: aiq.hint ? 0.75 : 0 }}>
          <HtmlWithLatex html={aiq.feedback} />
        </Box>
      ) : null}
      {aiq.hint && !anyConflict && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
          <HintIcon sx={{ fontSize: 15, color: '#d97706', mt: 0.2, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: '#92400e', fontStyle: 'italic', lineHeight: 1.5 }}>
            <strong>Tip:</strong> {aiq.hint}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const StudentMockExamReview = () => {
  // Click any figure on the review to enlarge it (question diagrams, answer images, drawings).
  const [zoomSrc, setZoomSrc] = useState(null);
  const handleReviewImageClick = useCallback((e) => {
    const img = e.target && e.target.closest ? e.target.closest('img') : null;
    if (!img || img.closest('button, a, [role="button"], [data-no-zoom]')) return;
    if ((img.naturalWidth && img.naturalWidth < 40) || (img.naturalHeight && img.naturalHeight < 24)) return;
    const src = img.currentSrc || img.getAttribute('src');
    if (src) setZoomSrc(src);
  }, []);
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reviewTab, setReviewTab] = useState('questions');

  // AI feedback state
  const [aiFeedbackStatus, setAiFeedbackStatus] = useState('pending');
  const [aiFeedbackMap, setAiFeedbackMap] = useState({});
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  // aiPercentage is fetched but display is computed inline from aiScore/total_marks
  const [, setAiPercentage] = useState(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const fetchReview = useCallback(async () => {
    if (!attemptId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/review`);
      const reviewData = res.data?.data ?? res.data;
      setData(reviewData);
      if (reviewData?.ai_feedback_status) {
        setAiFeedbackStatus(reviewData.ai_feedback_status);
      }
      if (reviewData?.ai_score != null) {
        setAiScore(reviewData.ai_score);
      }
    } catch (err) {
      const body = err.response?.data;
      const detail = body?.data?.error;
      setError(
        detail && detail !== 'An error occurred.'
          ? detail
          : (body?.message && body.message !== 'Error' ? body.message : 'Something went wrong loading this review. Please try again or contact support.')
      );
      toast.error('Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  const fetchAiFeedback = useCallback(async () => {
    if (!attemptId) return;
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/ai-feedback`);
      const payload = res.data?.data ?? res.data;
      const status = payload?.status ?? 'pending';
      setAiFeedbackStatus(status);
      if (status === 'completed') {
        setAiFeedbackMap(payload?.question_feedback ?? {});
        setAiOverall(payload?.overall ?? null);
        setAiScore(payload?.ai_score ?? null);
        setAiPercentage(payload?.ai_percentage ?? null);
        clearInterval(pollRef.current);
        // Re-fetch review data so the score header reflects the AI-adjusted score
        try {
          const reviewRes = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/review`);
          const reviewData = reviewRes.data?.data ?? reviewRes.data;
          setData(reviewData);
        } catch {
          // Non-critical: keep existing review data
        }
      } else if (status === 'failed') {
        clearInterval(pollRef.current);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, [attemptId]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  // Start polling for AI feedback
  useEffect(() => {
    if (!attemptId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        return;
      }
      if (aiFeedbackStatus === 'completed' || aiFeedbackStatus === 'failed') {
        clearInterval(pollRef.current);
        return;
      }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to the question specified in the URL hash after data loads
  useEffect(() => {
    if (!loading && data && hash) {
      const id = hash.replace(/^#/, '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading, data, hash]);

  useEffect(() => {
    if (aiFeedbackStatus === 'completed' || aiFeedbackStatus === 'failed') {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Card sx={{ p: 4, textAlign: 'center', maxWidth: 440 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <HintIcon sx={{ fontSize: 32, color: '#374151' }} />
          </Box>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Review could not be loaded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error || 'This attempt or review is not available. It may have been removed or you may not have access.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate(getMockExamPortalHomePath())}>
            Back to Mock Exams
          </Button>
        </Card>
      </Box>
    );
  }

  const { exam_name, score, total_marks, percentage, questions = [] } = data;
  const totalDisplayQuestions = (questions || []).length;
  const displayPercentage = aiScore != null && total_marks > 0
    ? Math.round((aiScore / total_marks) * 1000) / 10
    : percentage;

  const computeLeafMarks = (q, parent) =>
    resolveLeafVerdict(toMockLeafRow(q, parent), { aiFeedbackMap, aiFeedbackStatus });

  const breakdownRows = [];
  questions.forEach((q, index) => {
    const mainNum = index + 1;
    if (q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0) {
      q.sub_questions.forEach((sub, si) => {
        const m = computeLeafMarks(sub, q);
        breakdownRows.push({
          key: sub.id ?? `${mainNum}-${si}`,
          qLabel: si === 0 ? `Q${mainNum}` : '',
          partLabel: String.fromCharCode(97 + si).toUpperCase(),
          questionText: sub.question_text,
          ...m,
          isMainRow: si === 0,
        });
      });
    } else {
      const m = computeLeafMarks(q);
      breakdownRows.push({
        key: q.id ?? index,
        qLabel: `Q${mainNum}`,
        partLabel: '',
        questionText: q.question_text,
        ...m,
        isMainRow: true,
      });
    }
  });

  return (
    <Box
      onClick={handleReviewImageClick}
      sx={{ p: { xs: 1.5, sm: 2, md: 3 }, '& img:not([data-no-zoom])': { cursor: 'zoom-in' } }}
    >
      <Dialog open={!!zoomSrc} onClose={() => setZoomSrc(null)} maxWidth={false}>
        <Box sx={{ position: 'relative', bgcolor: '#fff' }}>
          <IconButton aria-label="Close" onClick={() => setZoomSrc(null)}
            sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, bgcolor: 'rgba(255,255,255,0.9)' }}>✕</IconButton>
          {zoomSrc && (
            <Box component="img" src={zoomSrc} alt="" data-no-zoom="1"
              sx={{ display: 'block', maxWidth: '95vw', maxHeight: '90vh', minWidth: { md: '50vw' }, objectFit: 'contain' }} />
          )}
        </Box>
      </Dialog>
      <Button size="small" sx={{ mb: 2 }} onClick={() => navigate(getMockExamPortalHomePath())}>
        Back to Mock Exams
      </Button>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {exam_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Question-by-question review
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(aiScore).toFixed(1)} / ${Number(total_marks).toFixed(1)}`}
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(displayPercentage).toFixed(1)}%`} variant="outlined" color="secondary" />
                <Chip
                  label={`Auto Score: ${Number(aiScore).toFixed(1)} / ${Number(total_marks).toFixed(1)}`}
                  variant="outlined"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                />
              </>
            ) : (
              <>
                <Chip label={`Score: ${Number(score).toFixed(1)} / ${Number(total_marks).toFixed(1)}`} color="primary" />
                <Chip label={`${Number(percentage).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing' ? (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: '#7c3aed' }} />}
                label="AI feedback generating…"
                size="small"
                sx={{ bgcolor: '#f3f0ff', color: '#7c3aed', border: '1px solid #e0d7ff' }}
              />
            ) : aiFeedbackStatus === 'completed' ? (
              <Chip
                icon={<AiIcon sx={{ fontSize: 14 }} />}
                label="AI feedback ready"
                size="small"
                sx={{ bgcolor: '#f3f0ff', color: '#7c3aed', border: '1px solid #e0d7ff' }}
              />
            ) : null}
          </Box>

          {/* AI Overall Summary */}
          {aiOverall && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#f3f0ff', border: '1px solid #e0d7ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AiIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                  AI Performance Summary
                </Typography>
                {aiOverall.performance_level && (
                  <Chip
                    label={aiOverall.performance_level.replace('_', ' ')}
                    size="small"
                    sx={{
                      bgcolor: {
                        excellent: '#dcfce7',
                        good: '#dbeafe',
                        average: '#fef9c3',
                        needs_improvement: '#fee2e2',
                        unknown: '#f3f4f6',
                      }[aiOverall.performance_level] ?? '#f3f4f6',
                      color: {
                        excellent: '#166534',
                        good: '#1e40af',
                        average: '#854d0e',
                        needs_improvement: '#991b1b',
                        unknown: '#374151',
                      }[aiOverall.performance_level] ?? '#374151',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {aiOverall.strengths?.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', display: 'block', mb: 0.5 }}>
                      ✓ Strengths
                    </Typography>
                    {aiOverall.strengths.map((s, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {aiOverall.weaknesses?.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#991b1b', display: 'block', mb: 0.5 }}>
                      ✗ Areas to Improve
                    </Typography>
                    {aiOverall.weaknesses.map((w, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6 }}>
                        • {w}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
              {aiOverall.recommendations?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 0.5 }}>
                    📚 Study Recommendations
                  </Typography>
                  {aiOverall.recommendations.map((r, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6 }}>
                      {i + 1}. {r}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Tabs value={reviewTab} onChange={(_, v) => setReviewTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab value="questions" label="Questions" />
        <Tab value="marks" label="Marks Breakdown" />
      </Tabs>

      {reviewTab === 'marks' && (
        <MarksBreakdownTable
          rows={breakdownRows}
          totalMarks={total_marks ?? 0}
          scoredMarks={aiScore != null ? aiScore : Number(score ?? 0)}
          title="Marks Breakdown"
          fileName={`${(exam_name || 'mock-exam').replace(/[^\w-]+/g, '_').slice(0, 60)}-marks`}
        />
      )}

      {reviewTab === 'questions' && (
      <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Questions {totalDisplayQuestions > 0 ? `(Total: ${totalDisplayQuestions})` : ''}
      </Typography>
      {questions.length === 0 ? (
        <Alert severity="info">No questions in this attempt.</Alert>
      ) : (
        questions.map((q, index) => {
          const prevSectionTitle = index > 0 ? questions[index - 1]?.section_title : null;
          const isNewSection = !!q.section_title && q.section_title !== prevSectionTitle;

          // ── Grouped question (shared stem + sub-questions) ──────────────────
          if (q.type === 'grouped') {
            const subs = q.sub_questions || [];
            const questionNumber = index + 1;

            // Resolve a single explanation video for the whole grouped question.
            // Prefer a sub-question video if present, otherwise fall back to the parent.
            const firstSubWithVideo = subs.find(
              (sq) => sq.explanation_video_url || sq.video_generation_status
            );
            const groupVideoUrl =
              q.explanation_video_url || firstSubWithVideo?.explanation_video_url || null;
            const groupVideoStatus =
              q.video_generation_status || firstSubWithVideo?.video_generation_status || null;

            return (
              <Fragment key={q.id}>
                {/* Section title divider — shown when section_title changes vs previous question */}
                {isNewSection && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: '#f3e5f5',
                      borderRadius: 1,
                      borderLeft: '3px solid #9c27b0',
                      '& ol': { pl: 2, m: 0 },
                      '& li': { mb: 0.5, fontSize: '0.875rem', color: '#6a1b9a', lineHeight: 1.6 },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: q.section_instructions ? 0.5 : 0 }}>
                      {q.section_title}
                    </Typography>
                    {q.section_instructions && (
                      <FormatInstructions text={q.section_instructions} typographySx={{ color: '#6a1b9a', fontSize: '0.875rem' }} />
                    )}
                  </Box>
                )}
                <Card id={`question-${q.id}`} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  {q.section_title && (
                    <Chip
                      label={q.section_title}
                      size="small"
                      sx={{ mb: 1.5, bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}
                    />
                  )}
                  {/* Shared stem header */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                    >
                      Shared Stem / Passage
                    </Typography>
                    {(() => {
                      const reconciled = reconcileRowFiguresForDisplay({
                        ...q,
                        question_text: cleanStemText(q.question_text),
                      });
                      return (
                        <>
                          <Box sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.7 }}>
                            {reconciled.html ? <HtmlWithLatex html={reconciled.html} /> : null}
                          </Box>
                          {reconciled.images.length > 0 ? (
                            <Box sx={{ mt: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                              {reconciled.images.map((url, idx) => (
                                <img
                                  key={idx}
                                  src={url}
                                  alt={reconciled.images.length > 1 ? `Shared passage diagram ${idx + 1}` : "Shared passage diagram"}
                                  style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 6, border: "1px solid #e0e0e0", display: "block" }}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              ))}
                            </Box>
                          ) : null}
                        </>
                      );
                    })()}
                  </Box>

                  {/* Sub-questions */}
                  {subs.map((sq, sqIdx) => {
                    const sqSubmitted = formatSubmittedWithSelectedIndex(sq, formatAnswer(sq.submitted_answer));
                    const sqCorrect = formatAnswer(sq.correct_answer);
                    const sqSubmittedArr = coerceFillAnswersArray(sq.submitted_answer, sq.correct_answer);
                    const sqCorrectArr = Array.isArray(sq.correct_answer) ? sq.correct_answer : null;
                    const sqIsTableQ = sq.type === 'table' && sq.answer_data?.table_layout?.grid?.length > 0;
                    // A table's answer is one value per CELL. The flat display below joins the whole
                    // list into a single string, which read as one number ("15") instead of the nine
                    // cells the student filled, so show tables cell-by-cell like a multi-fill.
                    const sqIsMultiFill = Array.isArray(sqCorrectArr) && sqCorrectArr.length > 1
                      ? (sqIsTableQ || isPerBlankFillDisplay(sq, sqSubmittedArr, sqCorrectArr))
                      : (!sqIsTableQ && isPerBlankFillDisplay(sq, sqSubmittedArr, sqCorrectArr));
                    const sqInterchangeable = fillBlanksAreInterchangeable(sq) || fillBlanksAreInterchangeable(q);
                    // The resolver is the only verdict. Reading `sqAiq.is_correct` here let the AI
                    // overturn a rule verdict on rows it never graded (multi-blank fills, drawings,
                    // answer_rule rows), which is exactly the drift resolveLeafVerdict exists to remove.
                    const sqLeaf = resolveLeafVerdict(toMockLeafRow(sq, q), { aiFeedbackMap, aiFeedbackStatus });
                    const sqRuleIsCorrect = sqLeaf.result === 'correct';
                    const sqRuleIsPartial = sqLeaf.result === 'partial';
                    const sqDisplayIsCorrect = sqRuleIsCorrect;
                    const sqBlankLabels = sq.answer_data?.blank_labels ?? [];
                    const sqIsHubSpokeOuter = sq.answer_data?.diagram_type === 'hub-spoke-outer';

                    const sqLabel = String.fromCharCode(97 + sqIdx);
                    const sqEmbeddedCorrect = sqIsMultiFill
                      ? null
                      : reviewYourAnswerWasCorrectLabel({
                          questionText: sq.question_text,
                          passageText: q.question_text,
                          correctAnswer: sq.correct_answer,
                          studentAnswer: sq.submitted_answer,
                          isCorrect: sq.is_correct === true || sqRuleIsCorrect,
                          isPartial: sqRuleIsPartial || sq.is_partial === true,
                          hasDrawing: !!sq.submitted_drawing,
                        });
                    // Strip repeated passage context (data table, etc.) from sub-question text
                    const sqCleanText = extractSubCore(sq.question_text, sqLabel);
                    const sqWithClean = { ...sq, question_text: sqCleanText };
                    const sqPaperQuestion =
                      isFillQuestionType(sq) &&
                      Array.isArray(sq.correct_answer) &&
                      !(sq.answer_data?.correct_blanks?.length > 0 || (sq.blanks?.length ?? 0) > 0)
                        ? { ...sqWithClean, answer_data: { ...(sqWithClean.answer_data || {}), correct_blanks: sq.correct_answer.map((a) => [a]) } }
                        : sqWithClean;

                    return (
                      <Box
                        key={sq.id}
                        sx={{
                          mb: sqIdx < (q.sub_questions.length - 1) ? 2 : 0,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 1,
                        }}
                      >
                        <ScanNoteAlert note={sq.scan_note} />
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          {sq.type?.replace(/-/g, ' ')}
                        </Typography>
                        <QuestionPaperLayout
                          question={sqPaperQuestion}
                          questionNumber={questionNumber}
                          parentNumber={questionNumber}
                          subLabel={sqLabel}
                          showAnswers={true}
                          marks={sq.marks}
                          studentAnswers={sqIsTableQ || sqIsMultiFill ? sqSubmittedArr : sq.submitted_answer}
                          hideImage={true}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <ImageFillReviewPair row={sq} parent={q} allCorrect={sqRuleIsCorrect} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {sqIsMultiFill ? (
                            /* ── Per-blank labeled display for multi-fill sub-question ── */
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Your answer → Correct answer
                                </Typography>
                                <VerdictChip leaf={sqLeaf} />
                                <LeafMarks leaf={sqLeaf} />
                              </Box>
                              {sqCorrectArr.map((correctVal, bIdx) => {
                                const studentVal = sqSubmittedArr[bIdx];
                                // For hub-spoke-outer use A/B/C/D; otherwise use the expression label
                                // Unlabelled blanks fall back to their position. Without it a
                                // multi-blank answer printed as a bare column of "value → value"
                                // rows with nothing saying which space each pair belonged to.
                                const blLabel = sqIsHubSpokeOuter
                                  ? (['A', 'B', 'C', 'D'][bIdx] ?? String.fromCharCode(65 + bIdx))
                                  : (sqBlankLabels[bIdx] || String(bIdx + 1));
                                // Match against the server's accepting set (answer_data.correct_blanks),
                                // not the single flattened display value — otherwise a blank credited via
                                // an alternative is painted red beside a chip saying it was correct.
                                // The server's verdict wins: a correct alternative answer (answer_variants)
                                // differs from the displayed scheme box-by-box and was painted red.
                                const blOk = sqRuleIsCorrect || blankIsCorrect(sq, bIdx, studentVal, correctVal, {
                                  pooled: sqInterchangeable,
                                  displayList: sqCorrectArr,
                                  submittedList: sqSubmittedArr,
                                });
                                return (
                                  <Box
                                    key={bIdx}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}
                                  >
                                    {blLabel && (
                                      <Box sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary', flexShrink: 0 }}>
                                        <HtmlWithLatex html={`${blLabel}${sqIsHubSpokeOuter ? ':' : ')'}`} />
                                      </Box>
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: studentVal == null || studentVal === ''
                                          ? 'text.secondary'
                                          : blOk ? 'success.main' : 'error.main',
                                      }}
                                    >
                                      {studentVal ?? '—'}
                                    </Typography>
                                    <Typography variant="body2" color="text.disabled">→</Typography>
                                    <Box sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                                      <HtmlWithLatex html={String(correctVal)} />
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            /* ── Standard flat display ── */
                            <>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Your answer
                                  </Typography>
                                  {sqEmbeddedCorrect ? (
                                    <Chip label="Correct" size="small" color="success" sx={{ fontWeight: 700, height: 22 }} />
                                  ) : (
                                    <VerdictChip leaf={sqLeaf} />
                                  )}
                                  <LeafMarks leaf={sqLeaf} />
                                </Box>
                                <Box
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    color: sqEmbeddedCorrect
                                      ? 'success.main'
                                      : (sq.submitted_answer == null || sq.submitted_answer === '') && !sq.submitted_drawing
                                      ? 'text.secondary'
                                      : sqDisplayIsCorrect
                                        ? 'success.main'
                                        : sqRuleIsPartial
                                          ? '#b45309'
                                          : sq.submitted_drawing
                                            ? 'warning.main'
                                            : 'error.main',
                                  }}
                                >
                                  <HtmlWithLatex html={plainLinesToHtml(typedPartOfAnswer(sq, sqSubmitted))} />
                                </Box>
                                {sq.submitted_drawing && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                                      Handwritten working:
                                    </Typography>
                                    <img
                                      src={sq.submitted_drawing}
                                      alt="Student handwritten working"
                                      style={{ maxWidth: '100%', border: '1px solid #e0e0e0', borderRadius: 4, display: 'block' }}
                                    />
                                  </Box>
                                )}
                              </Box>
                              {String(sq.correct_interactive?.kind || sq.answer_data?.interactive?.kind || '') !== 'glyph' ? (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {sqRuleIsPartial ? 'Full answer' : 'Correct answer'}
                                </Typography>
                                <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  <HtmlWithLatex html={completeTruncatedMixedCorrectAnswer(sqCorrect, sq.explanation) || sqCorrect} />
                                </Box>
                              </Box>
                              ) : null}
                            </>
                          )}
                          {(sq.correct_interactive || sq.answer_data?.interactive) && !isChoiceType(sq.type) && (
                            <InteractiveAnswerBlock row={sq} />
                          )}
                          <AiFeedbackPanel
                            questionId={sq.id}
                            aiFeedbackMap={aiFeedbackMap}
                            aiFeedbackStatus={aiFeedbackStatus}
                            questionMarks={sq.marks}
                            ruleIsCorrect={sqRuleIsCorrect}
                            ruleIsPartial={sqRuleIsPartial}
                            forceIncorrect={sqLeaf.result === 'incorrect' || sqLeaf.result === 'unanswered' || (sqLeaf.result === 'written' && !(sqLeaf.earned > 0))}
                            unanswered={sqLeaf.result === 'unanswered'}
                          />
                          {sq.explanation && (
                            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8faff', borderRadius: 2, border: '1px solid #e8ecff' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 0.5 }}>
                                Explanation
                              </Typography>
                              <Box sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                                <HtmlWithLatex html={sq.explanation} />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {subs.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No sub-questions found for this grouped question.
                    </Alert>
                  )}

                  {/* Single explanation video per grouped question */}
                  {groupVideoUrl && groupVideoStatus === 'completed' && (
                    <Box sx={{ mt: 2 }}>
                      <VideoExplanationPlayer
                        videoUrl={groupVideoUrl}
                        questionText={q.question_text || `Question ${questionNumber}`}
                      />
                    </Box>
                  )}
                  {groupVideoStatus && groupVideoStatus !== 'completed' && !groupVideoUrl && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Explanation video: {String(groupVideoStatus).replace(/_/g, ' ')}…
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              </Fragment>
            );
          }

          // ── Standalone question ─────────────────────────────────────────────
          const submitted = formatSubmittedWithSelectedIndex(q, formatAnswer(q.submitted_answer));
          const correct = formatAnswer(q.correct_answer);
          // For multi-fill: show per-blank answers with labels
          const submittedArr = coerceFillAnswersArray(q.submitted_answer, q.correct_answer);
          const correctArr = Array.isArray(q.correct_answer) ? q.correct_answer : null;
          const isTableQ = q.type === 'table' && q.answer_data?.table_layout?.grid?.length > 0;
          const isMultiFillQ = Array.isArray(correctArr) && correctArr.length > 1
            ? (isTableQ || isPerBlankFillDisplay(q, submittedArr, correctArr))
            : (!isTableQ && isPerBlankFillDisplay(q, submittedArr, correctArr));
          // Rule-based correctness — includes time (7.46=7:46pm), ordinals (3=3rd lap), etc.
          const interchangeable = fillBlanksAreInterchangeable(q);
          const leaf = resolveLeafVerdict(toMockLeafRow(q), { aiFeedbackMap, aiFeedbackStatus });
          const ruleIsCorrect = leaf.result === 'correct';
          const ruleIsPartial = leaf.result === 'partial';
          const displayIsCorrect = ruleIsCorrect;
          const questionNumber = index + 1;
          const qBlankLabels = q.answer_data?.blank_labels ?? [];
          const isHubSpokeOuterQ = q.answer_data?.diagram_type === 'hub-spoke-outer';
          const embeddedCorrectLabel = isMultiFillQ
            ? null
            : reviewYourAnswerWasCorrectLabel({
                questionText: q.question_text,
                correctAnswer: q.correct_answer,
                studentAnswer: q.submitted_answer,
                isCorrect: q.is_correct === true || ruleIsCorrect,
                isPartial: ruleIsPartial || q.is_partial === true,
                hasDrawing: !!q.submitted_drawing,
              });

          const paperQuestion =
            isFillQuestionType(q) &&
            Array.isArray(q.correct_answer) &&
            !(q.answer_data?.correct_blanks?.length > 0 || (q.blanks?.length ?? 0) > 0)
              ? { ...q, answer_data: { ...(q.answer_data || {}), correct_blanks: q.correct_answer.map((a) => [a]) } }
              : q;

          return (
            <Fragment key={q.id}>
              {/* Section title divider — shown when section_title changes vs previous question */}
              {isNewSection && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f3e5f5',
                    borderRadius: 1,
                    borderLeft: '3px solid #9c27b0',
                    '& ol': { pl: 2, m: 0 },
                    '& li': { mb: 0.5, fontSize: '0.875rem', color: '#6a1b9a', lineHeight: 1.6 },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: q.section_instructions ? 0.5 : 0 }}>
                    {q.section_title}
                  </Typography>
                  {q.section_instructions && (
                    <FormatInstructions text={q.section_instructions} typographySx={{ color: '#6a1b9a', fontSize: '0.875rem' }} />
                  )}
                </Box>
              )}
              <Card id={`question-${q.id}`} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                {q.section_title && (
                  <Chip
                    label={q.section_title}
                    size="small"
                    sx={{ mb: 1.5, bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}
                  />
                )}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {q.type?.replace(/-/g, ' ')}
                </Typography>
                <ScanNoteAlert note={q.scan_note} />
                <QuestionPaperLayout
                  question={paperQuestion}
                  questionNumber={questionNumber}
                  showAnswers={true}
                  marks={q.marks}
                  studentAnswers={isTableQ || isMultiFillQ ? submittedArr : q.submitted_answer}
                />
                <Divider sx={{ my: 1.5 }} />
                <ImageFillReviewPair row={q} allCorrect={ruleIsCorrect} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {isMultiFillQ ? (
                    /* ── Per-blank labeled display for multi-fill ── */
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          Your answer → Correct answer
                        </Typography>
                        <VerdictChip leaf={leaf} />
                        <LeafMarks leaf={leaf} />
                      </Box>
                      {correctArr.map((correctVal, bIdx) => {
                        const studentVal = submittedArr[bIdx];
                        // For hub-spoke-outer use A/B/C/D; otherwise use the expression label
                        // See the sub-question block above: an unlabelled blank is identified by
                        // its position so each "your answer → correct answer" row can be placed.
                        const label = isHubSpokeOuterQ
                          ? (['A', 'B', 'C', 'D'][bIdx] ?? String.fromCharCode(65 + bIdx))
                          : (qBlankLabels[bIdx] || String(bIdx + 1));
                        const blankOk = ruleIsCorrect || blankIsCorrect(q, bIdx, studentVal, correctVal, {
                          pooled: interchangeable,
                          displayList: correctArr,
                          submittedList: submittedArr,
                        });
                        return (
                          <Box
                            key={bIdx}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}
                          >
                            {label && (
                              <Box sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary', flexShrink: 0 }}>
                                <HtmlWithLatex html={`${label}${isHubSpokeOuterQ ? ':' : ')'}`} />
                              </Box>
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: studentVal == null || studentVal === ''
                                  ? 'text.secondary'
                                  : blankOk ? 'success.main' : 'error.main',
                              }}
                            >
                              {studentVal ?? '—'}
                            </Typography>
                            <Typography variant="body2" color="text.disabled">→</Typography>
                            <Box sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                              <HtmlWithLatex html={String(correctVal)} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    /* ── Standard flat display for MCQ / short-answer / etc. ── */
                    <>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            Your answer
                          </Typography>
                          {embeddedCorrectLabel ? (
                            <Chip label="Correct" size="small" color="success" sx={{ fontWeight: 700, height: 22 }} />
                          ) : (
                            <VerdictChip leaf={leaf} />
                          )}
                          <LeafMarks leaf={leaf} />
                        </Box>
                        <Box
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            color: embeddedCorrectLabel
                              ? 'success.main'
                              : (q.submitted_answer == null || q.submitted_answer === '') && !q.submitted_drawing
                              ? 'text.secondary'
                              : displayIsCorrect
                                ? 'success.main'
                                : ruleIsPartial
                                  ? '#b45309'
                                  : q.submitted_drawing
                                    ? 'warning.main'
                                    : 'error.main',
                          }}
                        >
                          <HtmlWithLatex html={plainLinesToHtml(typedPartOfAnswer(q, submitted))} />
                        </Box>
                        {q.submitted_drawing && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                              Handwritten working:
                            </Typography>
                            <img
                              src={q.submitted_drawing}
                              alt="Student handwritten working"
                              style={{ maxWidth: '100%', border: '1px solid #e0e0e0', borderRadius: 4, display: 'block' }}
                            />
                          </Box>
                        )}
                      </Box>
                      {String(q.correct_interactive?.kind || q.answer_data?.interactive?.kind || '') !== 'glyph' ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {ruleIsPartial ? 'Full answer' : 'Correct answer'}
                        </Typography>
                        <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          <HtmlWithLatex html={completeTruncatedMixedCorrectAnswer(correct, q.explanation) || correct} />
                        </Box>
                      </Box>
                      ) : null}
                    </>
                  )}

                  {(q.correct_interactive || q.answer_data?.interactive) && !isChoiceType(q.type) && (
                    <InteractiveAnswerBlock row={q} />
                  )}

                  {/* AI Feedback Panel */}
                  <AiFeedbackPanel
                    questionId={q.id}
                    aiFeedbackMap={aiFeedbackMap}
                    aiFeedbackStatus={aiFeedbackStatus}
                    questionMarks={q.marks}
                    ruleIsCorrect={ruleIsCorrect}
                    ruleIsPartial={ruleIsPartial}
                    forceIncorrect={leaf.result === 'incorrect' || leaf.result === 'unanswered' || (leaf.result === 'written' && !(leaf.earned > 0))}
                    unanswered={leaf.result === 'unanswered'}
                  />

                  {q.explanation && (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8faff', borderRadius: 2, border: '1px solid #e8ecff' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 0.5 }}>
                        Explanation
                      </Typography>
                      <Box sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                        <HtmlWithLatex html={q.explanation} />
                      </Box>
                    </Box>
                  )}

                  {q.explanation_video_url && q.video_generation_status === 'completed' && (
                    <Box sx={{ mt: 2 }}>
                      <VideoExplanationPlayer
                        videoUrl={q.explanation_video_url}
                        questionText={q.question_text || `Question ${questionNumber}`}
                      />
                    </Box>
                  )}
                  {q.video_generation_status && q.video_generation_status !== 'completed' && !q.explanation_video_url && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Explanation video: {q.video_generation_status.replace(/_/g, ' ')}…
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
            </Fragment>
          );
        })
      )}
      </>
      )}
    </Box>
  );
};

export default StudentMockExamReview;
